import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader, ROLE } from 'baseui/modal';
import type { Value } from 'baseui/select';
import { Select, TYPE } from 'baseui/select';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { USER_ALLOWED_ROLES } from '../../consts/user';
import { useCreateOrUpdateUser } from '../../hooks/use-create-or-update-user';
type UserManagementModalProps = {
  showUpdateModal: boolean;
  setShowUpdateModal: (showUpdateModal: boolean) => void;
};
const validateEmail = (email: string) => {
  return email.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
};
const UserManagementModal = (props: UserManagementModalProps) => {
  const {
    showUpdateModal,
    setShowUpdateModal
  } = props;
  const [email, setEmail] = React.useState<string>('');
  const [roles, setRoles] = React.useState<Value>([]);
  const queryClient = useQueryClient();
  const isFormValid = validateEmail(email) && roles.length > 0;
  const {
    isPending,
    createOrUpdateUser
  } = useCreateOrUpdateUser(email, roles.map(role => role.id!.toString()));
  const onSubmitUserRequest = React.useCallback(() => {
    if (!validateEmail(email) || roles.length < 0) {
      return;
    }
    createOrUpdateUser({
      email,
      roles: roles.map(role => role.id!.toString())
    }, {
      onSuccess: () => {
        toaster.positive('User operation done successfully', {
          key: 'createOrUpdateUserSuccess'
        });
        queryClient.invalidateQueries({
          queryKey: ['getUsers']
        });
        setShowUpdateModal(false);
      },
      onError: error => {
        toaster.negative(<>Failed creating or updating user: {error.message}</>, {
          key: 'createOrUpdateUserError'
        });
      }
    });
  }, [email, roles, createOrUpdateUser, setShowUpdateModal, queryClient]);
  if (isPending) {
    return <Spinner />;
  }
  return <Modal onClose={() => setShowUpdateModal(false)} closeable isOpen={showUpdateModal} animate autoFocus role={ROLE.dialog}>
      <ModalHeader>User Form</ModalHeader>
      <ModalBody>
        <FormControl label={() => 'Email'} caption={() => 'User will be created if it does not exist'}>
          <Input value={email} onChange={e => setEmail(e.target.value.toLowerCase().trim())} />
        </FormControl>
        <Select options={USER_ALLOWED_ROLES.map(role => ({
        id: role
      }))} labelKey="id" valueKey="id" placeholder="Choose roles" maxDropdownHeight="300px" type={TYPE.search} multi onChange={({
        value
      }: {
        value: Value;
      }) => {
        setRoles(value);
      }} value={roles} />
      </ModalBody>
      <ModalFooter>
        <ModalButton onClick={() => setShowUpdateModal(false)}>Cancel</ModalButton>
        <ModalButton disabled={!isFormValid} onClick={onSubmitUserRequest}>
          Create/Update
        </ModalButton>
      </ModalFooter>
    </Modal>;
};
export default UserManagementModal;